import { parseDate } from 'utils/format';
import queryString from 'utils/queryString';
import signature from 'utils/signature';
import stringify from 'utils/stringify';
import {
  apiClientGetRequest,
  apiClientPostRequest,
  apiClientPutRequest,
  apiClientDeleteRequest,
  apiClientHeadRequest,
} from '../lib/backendRequest';

const MIN_SYNC_VERSION = 3;
const ACTUAL_SYNC_VERSION = 4;

const SYNC_STATE = {
  IN_PROGRESS: 1,
  IN_CART: 2,
  ALREADY_ORDERED: 3,
};

export const formatSyncState = state => {
  switch (state) {
    case SYNC_STATE.IN_PROGRESS:
      return 'In progress';
    case SYNC_STATE.IN_CART:
      return 'Added to cart';
    case SYNC_STATE.ALREADY_ORDERED:
      return 'Already ordered';
    default:
      return 'Unknown';
  }
};

const formatData = (productId, templateUrl, templateVersion, data, draftName) => {
  return {
    version: ACTUAL_SYNC_VERSION,
    productId,
    templateUrl,
    templateVersion,
    data,
    draftName,
  };
};

const isDraftValid = (item, maxState = SYNC_STATE.ALREADY_ORDERED) => {
  // ignore higher state
  if (item.state > maxState) {
    return false;
  }

  // ignore older version
  const version = item.data?.version ?? 0;
  if (version < MIN_SYNC_VERSION) {
    return false;
  }

  // ignore empty pages
  const pages = item.data?.data?.pages ?? [];
  if (pages.length === 0) {
    return false;
  }
  return true;
};

const formatDraft = item => {
  /*
    draftProductId, we have to prefer draft.data.productId instead of draft.product_id
    - in some cases, draft.product_id is different to draft.data.productId
    - in some cases, draft.data.productId is missing, so we use draft.product_id
  */
  return {
    id: item.id,
    productId: item.data?.productId ?? item.product_id,
    data: item.data,
    state: item.state,
    created_at: item.created_at,
    updated_at: item.updated_at,
  };
};

/**
 *
 * @param {string} accessToken
 * @returns {object} SyncApi
 */
const syncApi = accessToken => ({
  /**
   * Creates new draft
   * @param {string} sku
   * @param {string} templateUrl
   * @param {string} templateVersion
   * @param {object} productData
   * @param {string} draftName
   * @returns {{ data: object, error: number }}
   */
  async saveProductDraft(sku, templateUrl, templateVersion, productData, draftName) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/sync',
        {
          product_id: sku,
          data: formatData(sku, templateUrl, templateVersion, productData, draftName),
        },
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          gzip: true,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Updates existing draft
   * @param {number} draftId
   * @param {string} sku
   * @param {string} templateUrl
   * @param {string} templateVersion
   * @param {object} productData
   * @param {string} draftName
   * @param {string} draftLastModified
   * @param {object} signal
   * @returns {{ data: object, error: number }}
   */
  async updateProductDraft(
    draftId,
    sku,
    templateUrl,
    templateVersion,
    productData,
    draftName,
    draftLastModified,
    signal,
  ) {
    const data = formatData(sku, templateUrl, templateVersion, productData, draftName);
    const signatureOnClient = signature(data);

    try {
      const response = await apiClientPutRequest(
        `api/v1/sync/${draftId}`,
        {
          data,
          last_modified: draftLastModified,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          signal,
          gzip: true,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      if (e.code === 400) {
        // Check draftLastModified vs last-modified on server
        // When draftLastModified is not set, skip this
        try {
          const response = await apiClientHeadRequest(`api/v1/sync/${draftId}`, {
            Authorization: `Token ${accessToken}`,
          });

          const lastModifiedOnServer = parseDate(
            response.headers.get('last-modified'),
            'ddd, DD MMM YYYY HH:mm:ss Z',
          );
          const lastModifiedOnClient = parseDate(draftLastModified);

          if (lastModifiedOnServer > lastModifiedOnClient) {
            const { data: serverDraftData } = await this.getProductDraft(draftId);
            let signatureOnServer;
            let draftLastModifiedOnServer;
            let serverHasSameData = false;
            if (serverDraftData) {
              draftLastModifiedOnServer = serverDraftData.updated_at;
              if (stringify(data) === stringify(serverDraftData.data)) {
                serverHasSameData = true;
              }
              signatureOnServer = signature(serverDraftData.data);
            }

            console.error(
              `SyncWarning - "updateProductDraft" - Draft #${draftId} is not actual, you probably rewrote some previous changes!`,
            );

            return {
              error: {
                ...e,
                draftLastModifiedOnServer,
                lastModifiedOnServer,
                lastModifiedOnClient,
                signatureOnClient,
                signatureOnServer,
                serverHasSameData,
              },
            };
          }
        } catch (ex) {
          console.error(
            `SyncWarning - "updateProductDraft" - HEAD request issue. Draft #${draftId}`,
            e,
          );
        }
      }

      return { error: e };
    }
  },

  /**
   * Updates draft state
   * @param {number} draftId
   * @param {string} state
   * @returns {{ data: object, error: number }}
   */
  async updateProductDraftState(draftId, state = 'cart') {
    try {
      const response = await apiClientPutRequest(
        `api/v1/sync/${draftId}/${state}`,
        {},
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getProductDrafts(skus = [], sinceId = undefined, maxState = SYNC_STATE.IN_CART) {
    try {
      const response = await apiClientGetRequest(
        queryString.stringifyUrl(`api/v1/sync`, {
          product_ids: skus.length === 0 ? undefined : skus.join(','),
          since_id: sinceId,
          limit: 25,
        }),
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      let nextPageSinceId;
      if (json.has_next_page && json.data.length > 0) {
        nextPageSinceId = json.data[json.data.length - 1].id;
      }

      const data = json.data.filter(item => isDraftValid(item, maxState)).map(formatDraft);

      // when we filter out all of the creations, we have to call it once again since last id
      if (data.length === 0 && nextPageSinceId) {
        return this.getProductDrafts(skus, nextPageSinceId, maxState);
      }

      return { data, nextPageSinceId };
    } catch (e) {
      return { error: e.body || e.code };
    }
  },

  async getProductDraft(draftId) {
    try {
      const response = await apiClientGetRequest(
        `api/v1/sync/${draftId}`,
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          retryInAnyCase: true,
        },
      );

      const json = await response.json();

      if (isDraftValid(json.data)) {
        return { data: formatDraft(json.data) };
      }
      return { error: 'Invalid Draft' };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Duplicates draft
   * @param {number} draftId
   * @returns {{ data: object, error: number }}
   */
  async duplicateProductDraft(draftId) {
    const { data: draft, error } = await this.getProductDraft(draftId);

    if (error) {
      return { error };
    }

    try {
      const response = await apiClientPostRequest(
        'api/v1/sync',
        {
          product_id: draft.productId,
          data: {
            ...draft.data,
            draftName: `Copy of ${draft.data.draftName || 'Unnamed creation'}`,
          },
        },
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          gzip: true,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e };
    }
  },

  async removeProductDraft(draftId) {
    try {
      const response = await apiClientDeleteRequest(`api/v1/sync/${draftId}`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: json };
    } catch (e) {
      return { error: e.code };
    }
  },
});

export default syncApi;
